/* variant-init-bridge.js
   Goal: If custom "availability" logic blocked the initial variant from
   being recognized, temporarily unblock only the matching controls,
   bounce once (or emit events), then restore.
*/
(function () {
  const LOG = false;
  const log = (...a) => LOG && console.log('[variant-init-bridge]', ...a);

  const afterLoad = (fn) => {
    if (document.readyState === 'complete') return setTimeout(fn, 250);
    window.addEventListener('load', () => setTimeout(fn, 250), { once: true });
  };

  function rootFor(el) {
    return el.closest('.shopify-section') || el.closest('.product') || document;
  }

  function getProductJSON(root) {
    try {
      const s =
        root.querySelector('script[type="application/json"][data-product]') ||
        root.querySelector('script[type="application/json"][data-product-json]') ||
        document.querySelector('script[type="application/json"][data-product]') ||
        document.querySelector('script[type="application/json"][data-product-json]');
      return s?.textContent ? JSON.parse(s.textContent) : null;
    } catch { return null; }
  }

  function getCurrentVariantId(form) {
    const url = new URL(window.location.href);
    return (url.searchParams.get('variant') || form.querySelector('[name="id"]')?.value || '').trim();
  }

  function emitAllVariantEvents(sectionId, variant) {
    // Try the common Ignite / theme event shapes
    const payload = { sectionId, variant };
    document.dispatchEvent(new CustomEvent('variant-change', { bubbles: true, detail: payload }));
    document.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: payload }));
    // Some themes also listen on the section node:
    const sec = document.getElementById(sectionId);
    if (sec) {
      sec.dispatchEvent(new CustomEvent('variant-change', { bubbles: true, detail: payload }));
      sec.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: payload }));
    }
  }

  function nudgeInputs(root) {
    root.querySelectorAll('select[name^="options["]').forEach(sel => {
      if (sel.value) {
        sel.dispatchEvent(new Event('input', { bubbles: true }));
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    const byName = new Map();
    root.querySelectorAll('input[type="radio"][name^="options["]').forEach(r => {
      if (!byName.has(r.name)) byName.set(r.name, []);
      byName.get(r.name).push(r);
    });
    byName.forEach(radios => {
      const checked = radios.find(r => r.checked);
      if (checked) {
        checked.dispatchEvent(new Event('input', { bubbles: true }));
        checked.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function unblockElementOnce(el) {
    if (!el) return () => {};
    const restore = {
      disabled: el.disabled,
      aria: el.getAttribute('aria-disabled'),
      pe: el.style.pointerEvents || null,
    };
    // Temporarily unblock
    if ('disabled' in el) el.disabled = false;
    el.setAttribute('aria-disabled', 'false');
    el.style.pointerEvents = 'auto';
    return () => {
      // Restore exactly what we changed
      if ('disabled' in el) el.disabled = !!restore.disabled;
      if (restore.aria === null) el.removeAttribute('aria-disabled');
      else el.setAttribute('aria-disabled', restore.aria);
      if (restore.pe === null) el.style.removeProperty('pointer-events');
      else el.style.pointerEvents = restore.pe;
    };
  }

  function controlsForOptionIndex(root, idx) {
    const swatches = Array.from(
      root.querySelectorAll(
        `[data-option-position="${idx + 1}"][data-option-value], [data-option-index="${idx + 1}"][data-option-value]`
      )
    );
    const select = root.querySelectorAll('select[name^="options["]')[idx] || null;
    const allRadios = Array.from(root.querySelectorAll('input[type="radio"][name^="options["]'));
    const names = Array.from(new Set(allRadios.map(r => r.name)));
    const radios = names[idx] ? allRadios.filter(r => r.name === names[idx]) : [];
    return { swatches, select, radios };
  }

  function clickValue(root, idx, value) {
    const { swatches, select, radios } = controlsForOptionIndex(root, idx);
    // swatch
    if (swatches.length) {
      const btn = swatches.find(b => {
        const v = b.getAttribute('data-option-value') || '';
        return v === String(value) || v.toLowerCase() === String(value).toLowerCase();
      });
      if (btn) { btn.click(); return true; }
    }
    // radio
    if (radios.length) {
      const r = radios.find(r =>
        !r.disabled && (
          String(r.value) === String(value) ||
          String(r.value).toLowerCase() === String(value).toLowerCase()
        )
      );
      if (r) {
        if (!r.checked) r.checked = true;
        r.dispatchEvent(new Event('input', { bubbles: true }));
        r.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    // select
    if (select) {
      const opt = Array.from(select.options).find(o =>
        !o.disabled && (
          String(o.value) === String(value) ||
          String(o.value).toLowerCase() === String(value).toLowerCase()
        )
      );
      if (opt) {
        select.value = opt.value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    return false;
  }

  function doInitForForm(form) {
    const root = rootFor(form);
    const product = getProductJSON(root);
    if (!product?.variants?.length) return true;

    const vid = getCurrentVariantId(form);
    const variant =
      product.variants.find(v => String(v?.id) === String(vid)) ||
      product.variants.find(v => v?.available) ||
      product.variants[0];
    if (!variant) return true;

    // Prepare unblocks for each option value we'll click
    const restorers = [];
    (variant.options || []).forEach((val, idx) => {
      const { swatches, radios, select } = controlsForOptionIndex(root, idx);
      // pick the element we intend to click for this value
      let el = null;
      if (swatches.length) {
        el = swatches.find(b => {
          const v = b.getAttribute('data-option-value') || '';
          return v === String(val) || v.toLowerCase() === String(val).toLowerCase();
        }) || null;
      } else if (radios.length) {
        el = radios.find(r => (r.value || '').toLowerCase() === String(val).toLowerCase()) || null;
      } else if (select) {
        el = select; // we'll set its value
      }
      if (el) restorers.push(unblockElementOnce(el));
    });

    // Click through the intended values
    (variant.options || []).forEach((val, idx) => clickValue(root, idx, val));
    nudgeInputs(root);

    // Emit theme events with the *actual* variant object
    const sectionId = (root.closest('.shopify-section') || root).id || 'main-product';
    emitAllVariantEvents(sectionId, variant);

    // Safety: make sure the submit buttons aren’t left disabled if variant is available
    if (variant.available) {
      const btn = form.querySelector('[type="submit"]');
      if (btn) {
        btn.removeAttribute('disabled');
        if (btn.getAttribute('aria-disabled') === 'true') btn.setAttribute('aria-disabled', 'false');
        btn.classList.remove('disabled');
      }
      const sticky = (form.closest('.section') || document).querySelector('.sticky-cta .button');
      if (sticky) {
        sticky.removeAttribute('disabled');
        if (sticky.getAttribute('aria-disabled') === 'true') sticky.setAttribute('aria-disabled', 'false');
        sticky.classList.remove('disabled');
      }
    }

    // Restore whatever we temporarily changed
    setTimeout(() => restorers.forEach(fn => fn && fn()), 600);
    return true;
  }

  function controlsPresent(root) {
    return !!(
      root.querySelector('[data-option-value]') ||
      root.querySelector('select[name^="options["]') ||
      root.querySelector('input[type="radio"][name^="options["]')
    );
  }

  function run() {
    document.querySelectorAll('product-form form').forEach(form => {
      const root = rootFor(form);
      const start = Date.now();

      const attempt = () => {
        if (Date.now() - start > 5000) return; // give up after ~5s
        if (!controlsPresent(root)) return setTimeout(attempt, 150);
        doInitForForm(form);
      };

      attempt();
    });
  }

  afterLoad(run);
})();
